import React from 'react';
import { Typography } from '@fiverr-private/typography';
import { translate as appTranslate } from '@fiverr-private/i18n-react';
import { BriefSection, DynamicSection, ViewSection, SectionTypes, FileAttachmentData } from '../../../../../types';
import { isSectionEmpty } from '../../../../../utils';
import { useBriefContext } from '../../../../../common/BriefContext';
import { ShortTextEdit, ShortTextView } from './ShortText';
import { TextareaEdit, TextareaView } from './Textarea';
import { AttachmentsView, AttachmentsEdit } from './Attachments';

const T_PREFIX = 'ai_brief.sections';

export interface BriefSectionContentProps<TContent = string> {
  section: BriefSection<TContent>;
  onChange: (content: TContent) => void;
  onAttachmentsChange: (attachments: FileAttachmentData[]) => void;
  onEditEnd: () => void;
  onCancel: () => void;
  shouldRemoveSection?: boolean;
}

export interface BriefSectionViewContentProps<TContent = string> {
  section: BriefSection<TContent>;
  startEditing?: () => void; // For ViewSection components
  forceUpdateContent?: (content: TContent) => void;
}

export const BriefSectionEditArea = (props: BriefSectionContentProps) => {
  switch (props.section.type) {
    case SectionTypes.shortText:
      return <ShortTextEdit {...props} />;
    case SectionTypes.attachments:
      return <AttachmentsEdit {...props} />;
    case SectionTypes.view:
      // ViewSection should never be in edit mode themselves
      return null;
    default: {
      if (
        (props.section.type === SectionTypes.dynamic || props.section.type === SectionTypes.metadata) &&
        (props.section as DynamicSection).EditComponent
      ) {
        const SectionEditComponent = (props.section as DynamicSection).EditComponent;
        return <SectionEditComponent {...props} />;
      }

      return <TextareaEdit {...props} />;
    }
  }
};

export const BriefSectionViewArea = (props: BriefSectionViewContentProps) => {
  const { section } = props;
  const { editedSectionId } = useBriefContext();
  const isEmpty = isSectionEmpty(section);

  if (isEmpty && section.type !== SectionTypes.attachments) {
    if (section.mandatory) {
      return <Typography color="grey_900">({appTranslate(`${T_PREFIX}.mandatory`)})</Typography>;
    } else if (!section.mandatory && editedSectionId !== section.id) {
      return <Typography color="grey_900">({appTranslate(`${T_PREFIX}.optional`)})</Typography>;
    }
  }

  switch (props.section.type) {
    case SectionTypes.shortText:
      return <ShortTextView {...props} />;
    case SectionTypes.attachments:
      return <AttachmentsView {...props} />;
    case SectionTypes.view: {
      const SectionViewComponent = (props.section as ViewSection).ViewComponent;
      return <SectionViewComponent {...props} />;
    }
    default:
      if (
        (props.section.type === SectionTypes.dynamic || props.section.type === SectionTypes.metadata) &&
        (props.section as DynamicSection).ViewComponent
      ) {
        const SectionViewComponent = (props.section as DynamicSection).ViewComponent!;
        return <SectionViewComponent {...props} />;
      }

      return <TextareaView {...props} />;
  }
};
